import _ from 'lodash'
import Url from 'url-parse'
import { v2Host } from '@/config/host'
import { getApiPath } from '@/common/api-proxy'
import { NODE_ENV } from '@com/config/env'
import { LivePreviewAudioMessage, LivePreviewImageMessage } from '@/types/dmaker/live-preview'
import JSONParse from '@/utils/JSONParse'
import { getDmakerImage } from '@/api/creative/template'
import { readBin } from '@/api/common/bin'
import generateUUID from '@/views/creative/utils/generate-uuid'
import { getFileName } from '@com/utils/file'
import { cropFont } from '@/api/common/font'
import qs from 'qs'
import { Font } from '@com/i18n'
import { defaultFont } from '../config'

export const baseUrl = `${v2Host}${getApiPath('/openapi/dmaker/bin')}`

// 不同环境需要的协议不一样
export function convertProtocol(url: string): string {
  // 测试环境与开发环境使用 http
  return ['development', 'test'].some(item => item === NODE_ENV)
    ? `${url.replace('https', 'http')}`
    : url
}

export function encodeImageBin(val: string[]): string {
  const text = JSON.stringify(val)
  return encodeURIComponent(text)
}

async function readImageBin({ binPath, resourceUrl }: {
  binPath: string;
  resourceUrl: string;
}): Promise<string> {
  let res: any = null
  try {
    res = await readBin({ url: binPath }, 1)
  } catch (err) {
    console.log(err, `读取${binPath}文件失败`)

    return encodeImageBin([
      JSON.stringify({
        uuid: generateUUID(),
        type: 3,
        source: [convertProtocol(resourceUrl)]
      })
    ])
  }
  const bin = JSONParse(_.get(res, 'data.data.body')) || ''
  const binMeta = JSONParse(bin[0])
  binMeta.source[0] = convertProtocol(resourceUrl)
  bin[0] = JSON.stringify(binMeta)
  return encodeImageBin(bin)
}

// 图片bin文件例子：["{\"uuid\":\"swazyJ5DZEzA701\",\"type\":3,\"source\":[\"http://mmp-portal-dev.s3.cn-north-1.amazonaws.com.cn/creative_v2/template/128/upload/normal/8fe535db55272804d02779c8d6d1bf71_xE3hMt1v9.png\"],\"padding\":{\"0\":[180,110,180,110]}}","{}",""]

/**
 * cookUpdateImageMessage 加工实时预览图片所需的信息
 * bin 图片原始的bin数据
 */
export async function cookUpdateImageMessage({
  targetResourcePath,
  newResourceUrl,
  baseUrl,
  binPath,
  visible,
  playableBaseUrl
}: {
  targetResourcePath: string;
  newResourceUrl: string;
  baseUrl: string;
  binPath: string;
  visible: boolean;
  playableBaseUrl: string;
}): Promise<LivePreviewImageMessage> {
  // 隐藏
  // 全等于 false 才认为是隐藏，其他值都是展示
  if (visible === false) {
    const dmakerImage = await getDmakerImage(
      playableBaseUrl + '/resource/' + targetResourcePath
    )
    newResourceUrl = _.get(dmakerImage, 'data.data.url')
  }

  const bin = await readImageBin({ binPath, resourceUrl: newResourceUrl })

  // 如果是 bin，就需要特殊加工，ps 引擎需要 bin 文件（qc-core.js 逻辑中对有 .bin 后缀判断）
  // https://gitlab.mobvista.com/playable/gm_endcard 项目的使用图片本身即可
  const url = `${baseUrl}?bin=${bin}&x=.bin`

  return {
    action: 'update',
    path: targetResourcePath,
    url,
    type: 0
  }
}

export async function cookUpdateFrameSequenceMessage({
  targetResourcePaths,
  newResourceUrls,
  baseUrl,
  visible,
  playableBaseUrl,
  filePath
}: {
  targetResourcePaths: string[];
  newResourceUrls: string[];
  baseUrl: string;
  visible: boolean;
  playableBaseUrl: string;
  filePath: string;
}) {
  const generateBin = (source: string[]) => {
    return [
      JSON.stringify({
        uuid: generateUUID(),
        type: 3,
        source
      })
    ]
  }
  const paths = targetResourcePaths.map((val: string) => {
    return new Url(val).pathname
  })

  let transparentImage = ''
  // 隐藏且还没有透明图时
  // 全等于 false 才认为是隐藏，其他值都是展示
  if (visible === false && !transparentImage) {
    // 要使用基础包原始图片，保证生产出来的图片的大小与基础包的一致
    const res = await getDmakerImage(`${playableBaseUrl}/resource/${filePath}/${getFileName(targetResourcePaths[0])}`)
    transparentImage = _.get(res, 'data.data.url')
  }

  const newUrls = newResourceUrls.map((val: string) => {
    const source = [convertProtocol(visible === false ? transparentImage : val)]
    return `${baseUrl}?bin=${encodeImageBin(generateBin(source))}&x=.bin`
  })

  return {
    action: 'update',
    path: paths,
    url: newUrls,
    type: 1
  }
}

/**
 * 加工 audio message
 */
export function cookUpdateAudioMessage({
  targetResourcePath,
  newResourceUrl
}: {
  targetResourcePath: string;
  newResourceUrl: string;
}): LivePreviewAudioMessage {
  return {
    action: 'update',
    path: targetResourcePath,
    url: `${convertProtocol(newResourceUrl)}`,
    type: 3
  }
}

const volumeSoundUrl = `${v2Host}${getApiPath('/openapi/dmaker/audio')}`
export function cookUpdateVolumeAudioMessage({
  targetResourcePath,
  newResourceUrl,
  volume,
  soundDisabled
}: {
  targetResourcePath: string;
  newResourceUrl: string;
  volume: number;
  soundDisabled: boolean;
}): LivePreviewAudioMessage {
  if (soundDisabled) {
    // 静音
    volume = -100
  }
  const newUrl = `${volumeSoundUrl}?volume=${volume}&url=${encodeURIComponent(
    newResourceUrl
  )}`
  return {
    action: 'update',
    path: targetResourcePath,
    url: `${convertProtocol(newUrl)}`,
    type: 3
  }
}

export async function getFontList(
  fontList: Font[]
): Promise<{ name: string; url: string }[]> {
  let res = []
  try {
    const promiseList = fontList
      .map(font => ({
        font: font.name,
        text: font.text.join('').replace(/\s/g, '')
      }))
      // 字符为空或默认字体不需要调用裁切接口
      .filter(font => {
        return font.text !== '' && font.font !== defaultFont
      })
      .map(font => cropFont(font))
    res = await Promise.all(promiseList)
  } catch (err) {
    return []
  }

  return res.map(item => {
    const { font } = qs.parse(_.get(item, 'config.data'))
    const fontBase64 = _.get(item, 'data.data.ttf')
    return {
      name: font as string,
      url: fontBase64
    }
  })
}
